import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Trophy, Loader2 } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import {
  GamificationMetricKey,
  SnapshotPeriod,
  YearToDateTotals,
  METRIC_LABELS,
} from "./types";

type RepRow = {
  userId: string;
  name: string;
  email: string | null;
  daily: SnapshotPeriod;
  weekly: SnapshotPeriod;
  monthly: SnapshotPeriod;
  yearToDate?: YearToDateTotals;
};

type RollUp = { reps: RepRow[] };

type SnapshotBucket = "daily" | "weekly" | "monthly";

/** In-tile toggle window. "year" is a totals-only view (no goal / pacing). */
type ToggleView = "daily" | "weekly" | "monthly" | "year";

export type DateRangeForGoals =
  | "today"
  | "yesterday"
  | "this-week"
  | "7d"
  | "this-month"
  | "30d"
  | "custom";

function dateRangeToBucket(dateRange: DateRangeForGoals): SnapshotBucket {
  switch (dateRange) {
    case "today":
    case "yesterday":
      return "daily";
    case "this-week":
    case "7d":
      return "weekly";
    case "this-month":
    case "30d":
    case "custom":
    default:
      return "monthly";
  }
}

function fillColor(pct: number): string {
  if (pct >= 1) return "bg-emerald-500";
  if (pct >= 0.7) return "bg-blue-500";
  if (pct >= 0.4) return "bg-amber-500";
  return "bg-rose-500";
}

interface ManagerGamificationTileProps {
  dateRange?: DateRangeForGoals;
  userFilter?: string;
  /**
   * When true (dashboard usage), render a self-contained Day / Month / Year
   * segmented toggle in the tile header that drives the displayed window.
   * When false/omitted (Team Activity usage), the externally-controlled
   * `dateRange` prop drives the window and no in-tile toggle is shown.
   */
  enableToggle?: boolean;
}

const TOGGLE_OPTIONS: { value: ToggleView; label: string }[] = [
  { value: "daily", label: "Day" },
  { value: "weekly", label: "Week" },
  { value: "monthly", label: "Month" },
  { value: "year", label: "Year" },
];

export function ManagerGamificationTile({ dateRange = "30d", userFilter = "all", enableToggle = false }: ManagerGamificationTileProps) {
  const bucket = dateRangeToBucket(dateRange);
  const [view, setView] = useState<ToggleView>("monthly");

  const { data, isLoading } = useQuery<RollUp>({
    queryKey: ["/api/gamification/manager-roll-up"],
    refetchInterval: 60_000,
  });

  // When the in-tile toggle is enabled, it owns the displayed window. Otherwise
  // fall back to the externally-controlled dateRange bucket (Team Activity).
  const isYear = enableToggle && view === "year";
  const periodBucket: SnapshotBucket = enableToggle
    ? view === "daily"
      ? "daily"
      : view === "weekly"
        ? "weekly"
        : "monthly"
    : bucket;

  const periodLabel = isYear
    ? "This Year"
    : periodBucket === "daily"
      ? "Today"
      : periodBucket === "weekly"
        ? "This Week"
        : "This Month";

  const visibleReps = (data?.reps ?? []).filter(
    (rep) => userFilter === "all" || rep.userId === userFilter
  );

  return (
    <Card data-testid="manager-gamification-tile">
      <CardContent className="p-4 space-y-3">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <Trophy className="h-4 w-4 text-amber-500" />
            <h3 className="text-sm font-semibold">Goals</h3>
          </div>
          {enableToggle && (
            <div
              className="inline-flex items-center rounded-md border bg-muted/50 p-0.5"
              role="group"
              data-testid="goals-period-toggle"
            >
              {TOGGLE_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setView(opt.value)}
                  aria-pressed={view === opt.value}
                  data-testid={`button-goals-period-${opt.value}`}
                  className={cn(
                    "px-2.5 py-1 text-xs font-medium rounded-sm transition-colors",
                    view === opt.value
                      ? "bg-background text-foreground shadow-sm"
                      : "text-muted-foreground hover:text-foreground",
                  )}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          )}
        </div>
        {isLoading && (
          <div className="flex justify-center py-6">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        )}
        {!isLoading && visibleReps.length === 0 && (
          <p className="text-xs text-muted-foreground py-3">
            {(data?.reps?.length ?? 0) === 0
              ? "No sales reps configured yet."
              : "No goals data for the selected user."}
          </p>
        )}
        {!isLoading && visibleReps.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-muted-foreground text-xs">
                <tr>
                  <th className="text-left font-medium pb-2">Rep</th>
                  {(["activity", "pipeline", "wins"] as GamificationMetricKey[]).map((m) => (
                    <th key={m} className="text-left font-medium pb-2 px-2">
                      {periodLabel} · {METRIC_LABELS[m]}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {visibleReps.map((rep) => (
                  <tr key={rep.userId} className="border-t" data-testid={`row-rep-${rep.userId}`}>
                    <td className="py-2 pr-2 font-medium truncate max-w-[180px]">{rep.name}</td>
                    {(["activity", "pipeline", "wins"] as GamificationMetricKey[]).map((m) => {
                      if (isYear) {
                        const ytd = rep.yearToDate;
                        const count = ytd ? ytd[m as "activity" | "pipeline" | "wins"] : 0;
                        return (
                          <td key={m} className="py-2 px-2" data-testid={`cell-${rep.userId}-${m}`}>
                            <span className="text-sm font-medium whitespace-nowrap" data-testid={`text-ytd-${rep.userId}-${m}`}>
                              {count}
                            </span>
                          </td>
                        );
                      }
                      const periodData =
                        periodBucket === "daily" ? rep.daily : periodBucket === "weekly" ? rep.weekly : rep.monthly;
                      const cell = periodData[m];
                      const pct = Math.min(1, cell.pct);
                      return (
                        <td key={m} className="py-2 px-2" data-testid={`cell-${rep.userId}-${m}`}>
                          <div className="flex items-center gap-2">
                            <div className="w-20 h-2 rounded-full bg-muted overflow-hidden">
                              <div
                                className={cn("h-full transition-all", fillColor(pct))}
                                style={{ width: `${pct * 100}%` }}
                              />
                            </div>
                            <span className="text-xs whitespace-nowrap">
                              {cell.count}/{cell.goal}
                            </span>
                            <Badge variant="outline" className="text-[10px]">
                              {cell.paceStatus === "ahead" ? "Ahead" : cell.paceStatus === "behind" ? "Behind" : "On track"}
                            </Badge>
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
